/**
 * Pozvánka „vyzkoušej si svůj obchod" na straně prohlížeče (KAN-761).
 *
 * Rozjezd tenanta nekončí zaškrtnutým checklistem, ale vlastním
 * nákupem. Checklist proto nabídne odkaz a QR kód; tenant projde cestou
 * zákazníka (upload → cena → košík → pokladna) a ta objednávka krok
 * „První objednávka" splní.
 *
 * Objednávka z téhle cesty musí vzniknout s ``is_test`` (KAN-748) —
 * jinak si každý tenant při rozjezdu ušpiní tržby, provize a statistiky.
 * Příznak z pokladny ale backend honoruje jen přihlášenému adminovi
 * vlastního tenanta, a QR se schválně načítá **mobilem**, kde žádná
 * admin session není. Podepsaný token z odkazu je tedy jediné, co na
 * telefonu o zkušebním nákupu ví; ``api/queries/checkout.ts`` ho k
 * objednávce přiloží.
 *
 * **localStorage, ne sessionStorage.** Cesta je dlouhá a vede přes
 * překlopení souboru, nacenění a pokladnu; košík ji přežije (cookie),
 * takže token musí taky. Kdyby umřel se zavřeným tabem, tenant by
 * dokončil nákup a mlčky si vyrobil ostrou objednávku — přesně ta
 * špinavá tržba, které se celá věc vyhýbá.
 *
 * **Razítko se zápisem**, ze stejného důvodu jako u
 * ``embed/guestSession``: ``localStorage`` samo o sobě expiraci nezná a
 * token bez ní by na tomhle prohlížeči označoval objednávky i za měsíc.
 * Doba se drží TTL serverového tokenu — dvě různá čísla by znamenala,
 * že jedno z nich lže.
 *
 * **Maže se po objednávce.** Účel splnil; token je jednorázová
 * pozvánka, ne přístup k něčemu (na rozdíl od handle košíku, který je
 * hostovým jediným klíčem k jeho objednávce, a proto se nemaže).
 */

/** Klíč fragmentu — musí sedět s ``STOREFRONT_TRIAL_FRAGMENT_KEY`` v BE. */
export const TRIAL_FRAGMENT_KEY = "trial";

const STORAGE_KEY = "forgmatic.storefront-trial";

/** Táž doba jako ``TRIAL_TOKEN_TTL_SECONDS`` v ``services/storefront_trial.py``. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Tvar, který server podepisuje: ``<org>.<razítko>.<hex digest>``. */
const TOKEN_RE = /^\d{1,12}\.\d{1,12}\.[0-9a-f]{64}$/;

interface StoredToken {
  /** Token sám. */
  t: string;
  /** ``Date.now()`` při zápisu. */
  w: number;
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function read(): string | null {
  let raw: string | null = null;
  try {
    raw = storage()?.getItem(STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const { t, w } = parsed as { t?: unknown; w?: unknown };
  if (typeof t !== "string" || !TOKEN_RE.test(t)) return null;
  if (typeof w !== "number" || !Number.isFinite(w)) return null;
  // Razítko v budoucnosti jsou přenastavené hodiny, ne důvod pozvánku
  // zahodit; počítá se jen stáří přes strop.
  if (Date.now() - w > MAX_AGE_MS) {
    clearTrialToken();
    return null;
  }
  return t;
}

/** Token pro tenhle prohlížeč, nebo ``null``. */
export function currentTrialToken(): string | null {
  return read();
}

/** Zapomeň pozvánku — po dokončené objednávce, nebo když vypršela. */
export function clearTrialToken(): void {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // Úložiště, které odmítá mazat, odmítlo i zápis.
  }
}

/**
 * Přijmi ``#trial=`` z URL a ukliď ho z adresního řádku.
 *
 * Vrací true, když se pozvánka přijala. Úklid je z téhož důvodu jako
 * u ``?claim=`` (KAN-506): token nemá co viset v historii ani
 * v ``Referer`` na všechno, kam stránka odkáže.
 */
export function adoptTrialTokenFromUrl(): boolean {
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return false;
  }
  const fragment = new URLSearchParams(url.hash.replace(/^#/, ""));
  const claimed = fragment.get(TRIAL_FRAGMENT_KEY);
  if (!claimed || !TOKEN_RE.test(claimed)) return false;

  try {
    storage()?.setItem(
      STORAGE_KEY,
      JSON.stringify({ t: claimed, w: Date.now() } satisfies StoredToken),
    );
  } catch {
    // Kvóta nebo zablokované úložiště. Bez zápisu pozvánka neplatí —
    // a neplatná pozvánka je lepší než pozvánka, o které klient tvrdí,
    // že ji má, a checkout na ní pak spadne.
    return false;
  }

  fragment.delete(TRIAL_FRAGMENT_KEY);
  const rest = fragment.toString();
  url.hash = rest ? `#${rest}` : "";
  window.history.replaceState(
    window.history.state,
    "",
    url.pathname + url.search + url.hash,
  );
  return true;
}
